<template>
    <el-drawer v-model="drawerVisible" :title="replaceMode ? '替换正文图片' : '当前文章图片资源'" size="560px">
        <div class="image-manager-toolbar">
            <el-input v-model="keyword" clearable placeholder="按文件名筛选" prefix-icon="Search" />
            <el-button type="primary" :loading="uploading" :disabled="!articleId || resources.length >= 50"
                @click="openUploadPicker">
                上传图片
            </el-button>
            <input ref="uploadInput" class="hidden-input" type="file" multiple
                accept="image/jpeg,image/png,image/webp,image/gif" @change="handleFiles">
        </div>

        <el-progress v-if="uploading" :percentage="uploadProgress" :stroke-width="8" />
        <el-alert v-if="resources.length >= 50" type="warning" :closable="false" title="当前文章已达到50张图片上限" />

        <div v-loading="loading" class="resource-grid">
            <el-empty v-if="!loading && filteredResources.length === 0" description="暂无图片资源" />
            <article v-for="resource in filteredResources" :key="resource.id" class="resource-card">
                <div class="resource-preview" @click="preview(resource)">
                    <img :src="resource.url" :alt="resource.name">
                    <span class="resource-status" :class="resource.status">
                        {{ resource.status === 'active' ? '已保存' : '临时' }}
                    </span>
                </div>
                <div class="resource-name" :title="resource.name">{{ resource.name }}</div>
                <div class="resource-meta">{{ formatBytes(resource.size) }} · {{ formatDate(resource.createdAt) }}</div>
                <div class="resource-reference" :class="{ used: getReferenceCount(resource) > 0 }">
                    {{ getReferenceCount(resource) > 0 ? `正文已使用 ${getReferenceCount(resource)} 次` : '正文未使用' }}
                </div>
                <div class="resource-actions">
                    <el-button link size="small" type="primary" @click="choose(resource)">
                        {{ replaceMode ? '替换' : '插入' }}
                    </el-button>
                    <el-button link size="small" @click="rename(resource)">重命名</el-button>
                    <el-button link size="small" type="danger" @click="remove(resource)">删除</el-button>
                </div>
            </article>
        </div>
    </el-drawer>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
    deleteArticleImage,
    fetchArticleImages,
    renameArticleImage,
    uploadArticleImage
} from '@/api/flow-article-image'

const props = defineProps({
    visible: { type: Boolean, default: false },
    articleId: { type: String, default: '' },
    referenceCounts: { type: Object, default: () => ({}) },
    replaceMode: { type: Boolean, default: false }
})
const emit = defineEmits(['update:visible', 'choose', 'locate', 'changed'])

// 当前文章的资源列表及抽屉交互状态。
const resources = ref([])
const keyword = ref('')
const loading = ref(false)
const uploading = ref(false)
const uploadProgress = ref(0)
const uploadInput = ref(null)

// 将抽屉内部显示状态同步回父组件的 v-model:visible。
const drawerVisible = computed({
    get: () => props.visible,
    set: value => emit('update:visible', value)
})

// 文件名筛选只作用于已加载的当前文章资源。
const filteredResources = computed(() => {
    const value = keyword.value.trim().toLowerCase()
    if (!value) return resources.value
    return resources.value.filter(item => (item.name || '').toLowerCase().includes(value))
})

/** 打开隐藏的原生文件选择器。 */
function openUploadPicker() {
    uploadInput.value?.click()
}

watch(() => props.visible, visible => {
    if (visible) load()
})

/** 从服务端刷新当前文章图片，接口已按上传时间倒序返回。 */
async function load() {
    if (!props.articleId) return
    loading.value = true
    try {
        const response = await fetchArticleImages(props.articleId)
        if (response.code === 200) resources.value = response.data || []
        else ElMessage.error(response.content || '图片资源加载失败')
    } catch (error) {
        ElMessage.error(error.content || '图片资源加载失败')
    } finally {
        loading.value = false
    }
}

/** 顺序上传用户选择的图片，避免短时间创建过多并发请求。 */
async function handleFiles(event) {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (!files.length) return
    if (resources.value.length + files.length > 50) {
        ElMessage.warning(`当前还可上传 ${Math.max(0, 50 - resources.value.length)} 张图片`)
        return
    }

    uploading.value = true
    uploadProgress.value = 0
    let completed = 0
    for (const file of files) {
        const error = validateFile(file)
        if (error) {
            ElMessage.warning(`${file.name}：${error}`)
            continue
        }
        try {
            const response = await uploadArticleImage(props.articleId, file, progress => {
                uploadProgress.value = Math.round(((completed + progress / 100) / files.length) * 100)
            })
            if (response.code === 200) {
                resources.value.unshift(response.data)
                emit('changed')
            } else {
                ElMessage.error(response.content || `${file.name} 上传失败`)
            }
        } catch (error) {
            ElMessage.error(error.content || `${file.name} 上传失败`)
        }
        completed++
        uploadProgress.value = Math.round((completed / files.length) * 100)
    }
    uploading.value = false
}

/** 在发起请求前完成类型、空文件和 10MB 大小校验。 */
function validateFile(file) {
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
    if (!allowed.includes(file.type)) return '仅支持JPG、PNG、WebP和GIF'
    if (file.size <= 0) return '文件不能为空'
    if (file.size > 10 * 1024 * 1024) return '单张图片不能超过10MB'
    return ''
}

/**
 * 合并当前编辑内容和已保存正文的引用数。
 * 取较大值可避免未保存移除操作绕过服务端引用保护。
 */
function getReferenceCount(resource) {
    const current = Number(props.referenceCounts[resource.id] || 0)
    return Math.max(current, Number(resource.referenceCount || 0))
}

/** 插入资源或将其作为当前图块的替换资源。 */
function choose(resource) {
    emit('choose', resource)
    drawerVisible.value = false
}

/** 在新窗口预览原始图片资源。 */
function preview(resource) {
    window.open(resource.url, '_blank', 'noopener')
}

/** 修改资源显示名，不同步修改正文中已经设置的 alt 文本。 */
async function rename(resource) {
    try {
        const result = await ElMessageBox.prompt('请输入新的图片名称，扩展名保持不变', '重命名图片', {
            inputValue: resource.name,
            inputPattern: /\S+/,
            inputErrorMessage: '文件名不能为空',
            confirmButtonText: '保存',
            cancelButtonText: '取消'
        })
        const response = await renameArticleImage(props.articleId, resource.id, result.value)
        if (response.code !== 200) {
            ElMessage.error(response.content || '重命名失败')
            return
        }
        Object.assign(resource, response.data)
        emit('changed')
        ElMessage.success('重命名成功')
    } catch (error) {
        if (error !== 'cancel' && error !== 'close') ElMessage.error(error.content || '重命名失败')
    }
}

/** 删除未引用资源；存在引用时通知父组件定位首个正文图块。 */
async function remove(resource) {
    const references = getReferenceCount(resource)
    if (references > 0) {
        emit('locate', resource.id)
        ElMessage.warning(`图片仍被正文引用 ${references} 次，请先从正文移除并保存文章`)
        return
    }

    try {
        await ElMessageBox.confirm('删除后文件不可恢复，确定删除该图片资源吗？', '删除图片', {
            type: 'warning',
            confirmButtonText: '删除',
            cancelButtonText: '取消'
        })
        const response = await deleteArticleImage(props.articleId, resource.id)
        if (response.code !== 200) {
            ElMessage.error(response.content || '删除失败')
            return
        }
        resources.value = resources.value.filter(item => item.id !== resource.id)
        emit('changed')
        ElMessage.success('删除成功')
    } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
            if (error && error.data && error.data.referenceCount) emit('locate', resource.id)
            ElMessage.error((error && error.content) || '删除失败')
        }
    }
}

/** 将字节数格式化为资源卡片使用的易读单位。 */
function formatBytes(value) {
    const size = Number(value || 0)
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
    return `${(size / 1024 / 1024).toFixed(1)} MB`
}

/** 将接口时间压缩为分钟精度显示。 */
function formatDate(value) {
    if (!value) return '-'
    return String(value).replace('T', ' ').slice(0, 16)
}

// 允许编辑页在直接上传完成后主动刷新资源抽屉。
defineExpose({ refresh: load })
</script>

<style scoped>
.image-manager-toolbar {
    display: flex;
    gap: 10px;
    margin-bottom: 14px;
}

.hidden-input {
    display: none;
}

.resource-grid {
    min-height: 240px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
    margin-top: 14px;
}

.resource-card {
    min-width: 0;
    padding: 10px;
    border: 1px solid var(--el-border-color);
    border-radius: 6px;
    background: var(--el-bg-color);
}

.resource-preview {
    position: relative;
    height: 150px;
    overflow: hidden;
    border-radius: 4px;
    background: var(--el-fill-color-light);
    cursor: zoom-in;
}

.resource-preview img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.resource-status {
    position: absolute;
    top: 6px;
    right: 6px;
    padding: 2px 6px;
    border-radius: 10px;
    color: #fff;
    font-size: 11px;
    background: #e6a23c;
}

.resource-status.active {
    background: #67c23a;
}

.resource-name {
    margin-top: 8px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-weight: 600;
}

.resource-meta,
.resource-reference {
    margin-top: 4px;
    color: var(--el-text-color-secondary);
    font-size: 12px;
}

.resource-reference.used {
    color: var(--el-color-primary);
}

.resource-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 6px;
}

@media (max-width: 720px) {
    .resource-grid {
        grid-template-columns: 1fr;
    }
}
</style>
